import { useEffect, useState } from 'react';
import { isNativeApp } from '../../../lib/native';
import type { ChatFile } from './types';

// Angehängte Dateien: einmal laden, dann anzeigen (Bild oder PDF) und auf Wunsch speichern.
export interface LoadedFile {
  file: ChatFile;
  blob: Blob;
  url: string; // blob:-Adresse für Vorschau und Speichern
  src: string; // Adresse auf dem Server
}

export class FileLoadError extends Error {
  constructor(public readonly status: number) {
    super('Datei kann nicht geladen werden');
  }
}

export async function loadChatFile(url: string, file: ChatFile): Promise<LoadedFile> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'same-origin' });
  } catch {
    throw new FileLoadError(0);
  }
  if (!res.ok) throw new FileLoadError(res.status);
  const blob = await res.blob();
  return { file, blob, url: URL.createObjectURL(blob), src: url };
}

// Bilder, die der Browser anzeigen kann (HEIC meist nicht)
export const isPreviewImage = (mime: string) => mime.startsWith('image/') && mime !== 'image/heic';

// Speichert die geladene Datei unter ihrem Namen (normaler Browser-Download). Die Android-App kann blob:-Adressen nicht
// speichern, dort lädt der Download-Manager die Datei noch einmal vom Server.
export function saveLoadedFile(f: LoadedFile) {
  const a = document.createElement('a');
  a.href = isNativeApp() ? `${f.src}${f.src.includes('?') ? '&' : '?'}download=1` : f.url;
  a.download = f.file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Gibt die blob:-Adresse frei, sobald die Vorschau geschlossen ist.
export function useOpenFile() {
  const [open, setOpen] = useState<(LoadedFile & { meta: string }) | null>(null);
  useEffect(() => {
    if (!open) return;
    return () => URL.revokeObjectURL(open.url);
  }, [open]);
  return [open, setOpen] as const;
}
